import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { DIRECTION, type Locale, MESSAGES, type Messages } from './messages';

interface LocaleContextValue {
  readonly locale: Locale;
  readonly messages: Messages;
  readonly setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

// Applies lang/dir to the document so layout, text direction and assistive
// technology follow the selected language.
export function LocaleProvider({ initial, children }: { readonly initial: Locale; readonly children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(initial);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = DIRECTION[locale];
  }, [locale]);
  const value = useMemo(() => ({ locale, messages: MESSAGES[locale], setLocale }), [locale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used inside LocaleProvider');
  return value;
}

// Feature-local bilingual dictionaries: each feature declares its strings for
// both locales in one object (the type forces the two to match) and reads the
// active one. UI wording is an engineering default reviewed with the HAA;
// business terminology follows approved wording where it exists.
export type Dictionary<K extends string> = Readonly<Record<'ar' | 'en', Readonly<Record<K, string>>>>;

export function useText<K extends string>(dictionary: Dictionary<K>): Readonly<Record<K, string>> {
  return dictionary[useLocale().locale];
}
